const LOOPBACK_HOST_PATTERN =
  /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/;
const PRIVATE_IPV4_HOST_PATTERN =
  /^(10\.\d{1,3}\.\d{1,3}\.\d{1,3}|192\.168\.\d{1,3}\.\d{1,3}|172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3})$/;

export const DEV_PREVIEW_HOSTNAME = 'ubezpieczenia.dev.datus.pl';
export const LOCAL_PREVIEW_HOSTNAME = 'localhost';
export const DEV_PREVIEW_STORAGE_KEY = 'tvpDevPreviewDomain';
export const DEFAULT_DEV_PREVIEW_DOMAIN_KEY = 'tvp';
const LEGACY_TVP_PREVIEW_DOMAIN_KEY = 'tvpubezpieczenia';

export const DOMAIN_HOSTS = {
  tvp: 'tvp.uspartner.pl',
  ubezpieczenia: 'ubezpieczenia.uspartner.pl',
  orange: 'orange.uspartner.pl',
  nais: 'nais.uspartner.pl'
};

export const DEV_PREVIEW_OPTIONS = [
  {
    value: 'tvp',
    label: 'tvp.uspartner.pl',
    hostname: DOMAIN_HOSTS.tvp
  },
  {
    value: 'nais',
    label: 'nais.uspartner.pl',
    hostname: DOMAIN_HOSTS.nais
  },
  {
    value: 'orange',
    label: 'orange.uspartner.pl',
    hostname: DOMAIN_HOSTS.orange
  },
  {
    value: 'ubezpieczenia',
    label: 'ubezpieczenia.uspartner.pl',
    hostname: DOMAIN_HOSTS.ubezpieczenia
  }
];

const normalizePreviewDomainKey = (value) =>
  value === LEGACY_TVP_PREVIEW_DOMAIN_KEY ? 'tvp' : value;

const getWindow = () => (typeof window !== 'undefined' ? window : undefined);

const getStorage = () => {
  const currentWindow = getWindow();

  if (!currentWindow) {
    return null;
  }

  try {
    return currentWindow.localStorage;
  } catch (error) {
    return null;
  }
};

const isValidPreviewDomainKey = (value) =>
  Boolean(
    value &&
    Object.prototype.hasOwnProperty.call(
      DOMAIN_HOSTS,
      normalizePreviewDomainKey(value)
    )
  );

export const getActualHostname = () => {
  const currentWindow = getWindow();

  return currentWindow ? currentWindow.location.hostname : '';
};

export const isDevPreviewHost = () => {
  const actualHostname = getActualHostname();

  return (
    actualHostname === DEV_PREVIEW_HOSTNAME ||
    actualHostname === LOCAL_PREVIEW_HOSTNAME
  );
};

export const getDevPreviewDomainKey = () => {
  if (!isDevPreviewHost()) {
    return null;
  }

  const storage = getStorage();
  const storedValue = storage && storage.getItem(DEV_PREVIEW_STORAGE_KEY);
  const normalizedStoredValue = normalizePreviewDomainKey(storedValue);

  return isValidPreviewDomainKey(normalizedStoredValue)
    ? normalizedStoredValue
    : DEFAULT_DEV_PREVIEW_DOMAIN_KEY;
};

export const setDevPreviewDomainKey = (value) => {
  const storage = getStorage();
  const normalizedValue = normalizePreviewDomainKey(value);

  if (!storage) {
    return;
  }

  if (!isValidPreviewDomainKey(normalizedValue)) {
    storage.removeItem(DEV_PREVIEW_STORAGE_KEY);
    return;
  }

  storage.setItem(DEV_PREVIEW_STORAGE_KEY, normalizedValue);
};

export const getHostname = () => {
  const actualHostname = getActualHostname();

  if (!isDevPreviewHost()) {
    return actualHostname;
  }

  const previewDomainKey = getDevPreviewDomainKey();

  return previewDomainKey ? DOMAIN_HOSTS[previewDomainKey] : actualHostname;
};

export const getHostnamePrefix = () => getHostname().split('.')[0];

export const isDevelopmentHost = () => {
  const hostname = getActualHostname();

  return (
    hostname === 'localhost' ||
    hostname === '[::1]' ||
    LOOPBACK_HOST_PATTERN.test(hostname) ||
    PRIVATE_IPV4_HOST_PATTERN.test(hostname)
  );
};

export const getAuthDomain = () => {
  const hostnamePrefix = getHostnamePrefix();

  if (hostnamePrefix === 'nais') {
    return 'nais';
  }

  if (hostnamePrefix === 'tvp') {
    return 'tvp';
  }

  return hostnamePrefix;
};

export const getBrandingContext = () => {
  const actualHostname = getActualHostname();
  const hostname = getHostname();
  const previewDomainKey = getDevPreviewDomainKey();
  const isOrange = hostname === DOMAIN_HOSTS.orange;
  const isInsurances = hostname === DOMAIN_HOSTS.ubezpieczenia;
  const isUbezpieczeniaDev = hostname === DEV_PREVIEW_HOSTNAME;
  const isNais = hostname === DOMAIN_HOSTS.nais;
  const isTvpUbezpieczenia = hostname === DOMAIN_HOSTS.tvp;
  const isLanDevelopment = isDevelopmentHost();
  const showFullDashboard =
    isUbezpieczeniaDev || (!isOrange && !isInsurances && !isNais);
  const showShooterProtect =
    isOrange ||
    isInsurances ||
    isTvpUbezpieczenia ||
    isUbezpieczeniaDev ||
    isLanDevelopment ||
    isNais;
  const showRowerzysta =
    isTvpUbezpieczenia || isOrange || isInsurances || isNais;

  let faviconPathPrefix = '';

  if (isOrange || isInsurances) {
    faviconPathPrefix = '/orange';
  } else if (isNais) {
    faviconPathPrefix = '/nais';
  }

  let pageTitle = 'TVP Ubezpieczenia';

  if (isOrange) {
    pageTitle = 'Orange Ubezpieczenia';
  } else if (isInsurances || isNais) {
    pageTitle = 'USP Ubezpieczenia';
  }

  return {
    actualHostname,
    hostname,
    hostnamePrefix: getHostnamePrefix(),
    authDomain: getAuthDomain(),
    previewDomainKey,
    isDevPreviewHost: isDevPreviewHost(),
    isOrange,
    isInsurances,
    isUbezpieczeniaDev,
    isNais,
    isTvpUbezpieczenia,
    isLanDevelopment,
    showFullDashboard,
    showShooterProtect,
    showRowerzysta,
    faviconPathPrefix,
    pageTitle
  };
};
